import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Bell } from '@phosphor-icons/react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToast } from '@/context/ToastContext';

// Hardcoded notification content mapping
// The 'text' field from DB is just a key to look up the actual content
interface NotificationContent {
  title: string;
  getMessage: (agreementId?: string) => ReactNode;
}

const NOTIFICATION_CONTENT: Record<string, NotificationContent> = {
  rebate_early_payment: {
    title: 'Early Payment Discount Available',
    getMessage: (agreementId) => (
      <>
        Your rebate agreement for{' '}
        <span className="text-blue-600 underline cursor-pointer">Lumina</span>{' '}
        has an extra discount if you pay within 45 days. You have{' '}
        <span className="font-semibold text-orange-600">3 days left</span>!{' '}
        {agreementId && (
          <Link
            to="/rebates/agreements/$id"
            params={{ id: agreementId }}
            className="text-primary underline hover:text-primary/80"
          >
            View Agreement
          </Link>
        )}
      </>
    ),
  },
};

// Default content for unknown notification keys
const DEFAULT_CONTENT: NotificationContent = {
  title: 'Notification',
  getMessage: () => 'You have a new notification.',
};

// Demo payment amount
const DEMO_PAYMENT_AMOUNT = 12847.5;

// Demo payment history
const PAYMENT_HISTORY = [
  { date: '2025-12-15', amount: 8450.0, status: 'Completed', method: 'ACH' },
  { date: '2025-09-22', amount: 11230.75, status: 'Completed', method: 'ACH' },
  { date: '2025-06-10', amount: 9875.25, status: 'Completed', method: 'Wire' },
  { date: '2025-03-05', amount: 7650.0, status: 'Completed', method: 'ACH' },
];

type NotificationBellProps = {
  variant?: 'icon' | 'dot';
  className?: string;
};

export function NotificationBell({
  variant = 'icon',
  className,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<
    string | null
  >(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const { showToast } = useToast();

  // Filter out dismissed notifications for display
  const notifications: Array<{
    id: string;
    text: string;
    status: 'unread' | 'read';
    metadata: { agreementId: string | undefined };
    createdAt: Date;
  }> = dismissedIds.has('demo-rebate-early-payment')
    ? []
    : [
        {
          id: 'demo-rebate-early-payment',
          text: 'rebate_early_payment',
          status: 'unread',
          metadata: { agreementId: undefined },
          createdAt: new Date(),
        },
      ];
  const unreadCount = notifications.filter((n) => n.status === 'unread').length;
  const isLoading = false;

  const handlePayNowClick = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation(); // Prevent notification click
    setSelectedNotificationId(notificationId);
    setShowPaymentDialog(true);
  };

  const handleConfirmPayment = () => {
    // Dismiss the notification (frontend-only for demo)
    if (selectedNotificationId) {
      setDismissedIds((prev) => new Set(prev).add(selectedNotificationId));
    }
    setShowPaymentDialog(false);
    setSelectedNotificationId(null);
    setOpen(false); // Close the popover
    showToast({
      title: 'Payment Initiated',
      message: `ACH payment of $${DEMO_PAYMENT_AMOUNT.toLocaleString()} has been initiated successfully.`,
      color: 'success',
    });
  };

  if (variant === 'dot' && unreadCount === 0) {
    return null;
  }

  const trigger =
    variant === 'dot' ? (
      <button
        type="button"
        className={cn(
          'relative flex size-2 items-center justify-center rounded-full bg-red-500',
          className,
        )}
        aria-label="Notifications"
      >
        <span className="sr-only">Notifications</span>
      </button>
    ) : (
      <Button
        variant="ghost"
        size="icon"
        className={cn('relative h-7 w-7', className)}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        side="right"
        sideOffset={8}
      >
        <div className="border-b px-3 py-2">
          <h3 className="font-semibold text-sm">Notifications</h3>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No notifications
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const content =
                  NOTIFICATION_CONTENT[notification.text] ?? DEFAULT_CONTENT;
                // Get agreementId from notification metadata
                const agreementId = notification.metadata?.agreementId;

                return (
                  <div
                    key={notification.id}
                    className={cn(
                      'w-full px-3 py-3 text-left transition-colors hover:bg-accent',
                      notification.status === 'unread' &&
                        'bg-blue-50/50 dark:bg-blue-950/20',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {notification.status === 'unread' && (
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                      )}
                      <div
                        className={cn(notification.status === 'read' && 'pl-4')}
                      >
                        <p className="text-sm font-medium">{content.title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {content.getMessage(agreementId)}
                        </p>
                        {notification.text === 'rebate_early_payment' && (
                          <Button
                            size="sm"
                            className="mt-2 h-7 text-xs"
                            onClick={(e) =>
                              handlePayNowClick(e, notification.id)
                            }
                          >
                            Pay Now
                          </Button>
                        )}
                        <p className="mt-1 text-[10px] text-muted-foreground/70">
                          {new Date(
                            notification.createdAt,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>

      {/* Payment Confirmation Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm ACH Payment</DialogTitle>
            <DialogDescription>
              Are you sure you want to send{' '}
              <span className="font-semibold text-foreground">
                ${DEMO_PAYMENT_AMOUNT.toLocaleString()}
              </span>{' '}
              via ACH for your{' '}
              <span className="text-blue-600 underline cursor-pointer">
                Lumina
              </span>{' '}
              rebate agreement?
            </DialogDescription>
          </DialogHeader>

          {/* Transfer Time Info */}
          <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 p-3 text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-100">
              Estimated Transfer Time
            </p>
            <p className="text-blue-700 dark:text-blue-300">
              1-3 business days via ACH transfer
            </p>
          </div>

          {/* Payment History Table */}
          <div>
            <p className="text-sm font-medium mb-2">Payment History</p>
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Date</th>
                    <th className="px-3 py-2 text-left font-medium">Amount</th>
                    <th className="px-3 py-2 text-left font-medium">Method</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {PAYMENT_HISTORY.map((payment, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(payment.date).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2">
                        ${payment.amount.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {payment.method}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPaymentDialog(false)}
            >
              No
            </Button>
            <Button onClick={handleConfirmPayment}>Yes, Send Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Popover>
  );
}
